import React from 'react';
import PropTypes from 'prop-types';
import { Col, Row, OverlayTrigger, Tooltip } from 'react-bootstrap';
import { InputGroup, Button, Alert, Icon } from '@storable/canaveral-components';
import gql from 'graphql-tag';
import cx from 'classnames';
import BreadcrumbSettingsModal from '../../containers/BreadcrumbSettingsModal';
import { BLOG_PAGE, BLOG_CATEGORY_PAGE, HOME_PAGE } from '../../../src/shared/pageTypes';
import styles from './styles.scss';

const propTypes = {
  breadcrumbTrail: PropTypes.shape({
    name: PropTypes.string.isRequired,
  }),
  slug: PropTypes.string,
  toggleEditBreadcrumb: PropTypes.func.isRequired,
  editingBreadcrumb: PropTypes.bool.isRequired,
  pageType: PropTypes.shape({
    code: PropTypes.string.isRequired,
  }).isRequired,
  id: PropTypes.string.isRequired,
  domain: PropTypes.string.isRequired,
  title: PropTypes.string.isRequired,
  site: PropTypes.shape({
    customCompanyName: PropTypes.string,
  }).isRequired,
};
const defaultProps = {
  breadcrumbTrail: undefined,
  slug: undefined,
};

const overlay = (
  <Tooltip id="breadcrumbs-disabled">
    {"This page's breadcrumbs cannot be edited"}
  </Tooltip>
);

const isDisabledType = code => [HOME_PAGE, BLOG_PAGE, BLOG_CATEGORY_PAGE].includes(code);

const BreadcrumbSettings = ({
  breadcrumbTrail,
  slug,
  toggleEditBreadcrumb,
  editingBreadcrumb,
  pageType: { code },
  id,
  domain,
  title,
  site: { customCompanyName },
}) => {
  let breadcrumbName = title;
  let hasDrafts = false;

  if (isDisabledType(code)) {
    breadcrumbName = customCompanyName;
  } else if (breadcrumbTrail) {
    const trail = breadcrumbTrail.ancestorPages.map((page) => {
      if (!page.live) { hasDrafts = true; }
      return `${page.breadcrumbTrail.name} / `;
    }).join('');

    breadcrumbName = `${customCompanyName} / ${trail}${breadcrumbTrail.name}`;
  }

  return (
    <React.Fragment>
      <Col xs={6} className={styles.breadcrumbCol}>
        <Row className={styles.breadcrumbRow}>
          <label className={styles.heading}>
            Breadcrumbs and Custom URL
            <InputGroup className={styles.input}>
              <InputGroup.Text
                data-testid="breadcrumb-name"
                type="text"
                value={breadcrumbName}
                disabled
              />
            </InputGroup>
          </label>
        </Row>

        <Row className={styles.breadcrumbRow}>
          <div className={styles.customContainer}>
            <InputGroup className={styles.input}>
              <InputGroup.Text
                type="text"
                value={`${domain}${slug}`}
                disabled
              />
            </InputGroup>
          </div>
        </Row>
        {hasDrafts && (
          <Row>
            <Alert context="info" className={styles.alert}>
              <Icon id="info" label="draft warning" className={styles.infoIcon} />
              <div className={styles.infoText}>
                This breadcrumb structure contains one or more drafts.  This is a preview
                of what the breadcrumbs will be when all pages are live.
              </div>
            </Alert>
          </Row>
        )}
      </Col>
      <Col xs={6} className={styles.editBreadcrumbButton}>
        <OverlayTrigger overlay={overlay} trigger={isDisabledType(code) ? ['hover', 'focus'] : null}>
          <Button
            context="neutral"
            onClick={isDisabledType(code) ? undefined : toggleEditBreadcrumb}
            disabled={editingBreadcrumb}
            className={cx(isDisabledType(code) && styles.disabled)}
          >
             Edit
          </Button>
        </OverlayTrigger>
      </Col>
      {editingBreadcrumb && (
        <BreadcrumbSettingsModal show onDismiss={toggleEditBreadcrumb} id={id} domain={domain} />
      )}
    </React.Fragment>
  );
};

BreadcrumbSettings.propTypes = propTypes;
BreadcrumbSettings.defaultProps = defaultProps;

BreadcrumbSettings.fragments = {
  page: gql`
    fragment BreadcrumbSettingsPage on Page {
      title
      slug
      pageType {
        code
      }
      breadcrumbTrail {
        id
        name
        ancestorPages {
          id
          live
          breadcrumbTrail {
            id
            name
          }
        }
      }
    }
  `,
  site: gql`
    fragment BreadcrumbSettingsSite on Site {
      customCompanyName
    }
  `,
};

export default BreadcrumbSettings;
